import { useCallback, useMemo, useState, type ReactElement } from 'react';
import { computeBudget, rawNumber, type TaskId } from '@bigint-project/core';

import { CostTable } from '../components/cost';
import { buildCostTableRows } from '../integration/adapters';
import { useProjectStore } from '../store/project-store';
import { EmptyState } from './EmptyState';
import { selectCurrentCostResult } from '../integration/derivations';
import { formatMoney } from '../components/resource/format';

/** 成本表視圖：任務層級的成本三分法與基準線差異。 */
export function CostTableView(): ReactElement {
  const project = useProjectStore((state) => state.project);
  const [collapsed, setCollapsed] = useState<readonly TaskId[]>([]);

  const rows = useMemo(() => buildCostTableRows(project), [project]);
  const budget = useMemo(() => computeBudget(project), [project]);
  const currentCost = useMemo(() => selectCurrentCostResult(project), [project]);
  const hasBudgetCost = project.budgetAssignments?.some((assignment) => assignment.budgetCost !== undefined) ?? false;

  const toggleCollapse = useCallback((id: TaskId) => {
    setCollapsed((current) => (current.includes(id) ? current.filter((v) => v !== id) : [...current, id]));
  }, []);

  return (
    <div data-testid="cost-table-view" className="flex h-full w-full flex-col overflow-hidden rounded border border-slate-200 dark:border-slate-700">
      <section aria-label="成本與專案預算" className="flex flex-wrap gap-x-6 gap-y-1 border-b border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800/40">
        <span>
          目前預估：<strong>{currentCost === null ? '—' : formatMoney(rawNumber(currentCost.total.cost), project.currency)}</strong>
        </span>
        <span>
          專案預算：<strong>{hasBudgetCost ? formatMoney(rawNumber(budget.totalCost), project.currency) : '—'}</strong>
        </span>
        <span>
          差異：<strong>{currentCost === null || !hasBudgetCost ? '—' : formatMoney(rawNumber(currentCost.total.cost) - rawNumber(budget.totalCost), project.currency)}</strong>
        </span>
      </section>
      <div className="min-h-0 flex-1">
        {rows.length === 0 ? (
          <EmptyState title="尚無任務" description="新增任務後即可在此檢視成本明細。" testId="cost-table-empty" />
        ) : (
          <CostTable rows={rows} currency={project.currency} collapsedTaskIds={collapsed} onToggleCollapse={toggleCollapse} />
        )}
      </div>
    </div>
  );
}
